/**
 * DESIGN_SYSTEM.md §1.1, "Derived, computed at runtime - never hardcode":
 *
 *   shade(hex, f) = multiply each RGB channel by f, clamp 255.
 *
 * Used for the three derived values the spec names, all keyed off a game's
 * tint - which is creator data, not a token (§12, NFR-13):
 *   - blob shade under a tinted object   shade(gameColor, 0.34)
 *   - second blob shade                  shade(gameColor, 0.24)
 *   - card art wash behind a sprite      shade(gameColor, 0.16)
 */

export const BLOB_SHADE = 0.34;
export const BLOB_SHADE_2 = 0.24;
export const CARD_WASH = 0.16;

const clamp255 = (n: number): number => Math.min(255, Math.max(0, Math.round(n)));

export function shade(hex: string, f: number): string {
  const match = /^#?([0-9a-f]{6})$/i.exec(hex.trim());
  if (!match) {
    throw new Error(`shade() expects a 6-digit hex colour, got ${JSON.stringify(hex)}`);
  }
  const value = parseInt(match[1], 16);
  const r = clamp255(((value >> 16) & 0xff) * f);
  const g = clamp255(((value >> 8) & 0xff) * f);
  const b = clamp255((value & 0xff) * f);
  return `#${((r << 16) | (g << 8) | b).toString(16).padStart(6, "0")}`;
}

/**
 * The wash behind a card that *has* cover art — `game-cover-art`, 2026-09-13.
 *
 * Not from §1.1. The three factors above exist to push a tint far enough down
 * that a pixel sprite reads on top of it; `CARD_WASH` at 0.16 turns `#6FA86A`
 * into `#11190f`, which is near-black on purpose.
 *
 * A cover is a different job. Adam's reason for wanting the blur gone: "we can
 * pick a background color on the game, and then we upload a graphic of the
 * game, which is a square, we can choose a nice color to go along the graphic."
 * A square cover in a wider zone leaves the wash showing either side, and that
 * gap is now the point — so the colour has to be the colour that was picked,
 * not a near-black derivative of it.
 *
 * **This is the number to turn.** 1 is the tint undimmed; drop it toward 0.16
 * and covered cards fade back toward looking like sprite cards.
 */
export const COVER_WASH = 1;
